import { Cancel, Clear, Download, UploadOutlined } from "@mui/icons-material";
import { DatePicker, DateTimePicker, LocalizationProvider } from "@mui/lab";
import AdapterLuxon from "@mui/lab/AdapterLuxon";
import { Autocomplete, Box, Checkbox, FormControl, FormControlLabel, InputProps, Radio, RadioGroup, Switch, SxProps, TextField } from "@mui/material";
import { DateTime } from "luxon";
import { Fragment, ReactElement, ReactNode, useState } from "react"
import { Colors } from "../../config/Constants";
import { CameraCaptureButton } from "./CameraCaptureButton";
import { convertBase64, resizeImageFn } from "./CommonUtils";
import { UploadModel } from "./Models";

// Constants and interfaces
export enum GbkFieldType {
  NUMBER,
  STRING,
  PASSWORD,
  SELECT,
  UPLOAD,
  DATE,
  DATE_TIME,
  CHECKBOX,
  RADIO,
  SELECTION_TABS,
  SWITCH
}
interface SelectOption {
  value: string | number;
  display: string;
  invisible?: boolean;
}
interface BaseFieldProps {
  editMode: boolean;
  label?: string | ReactElement;
  useInternalLabel?: boolean;
  size?: 'small' | 'medium';
  value?: any;
  updateFn?: (v: any, error?: string) => void;
  validateFn?: (v: any) => string;
  inputProps?: InputProps;
  displayPrefix?: string;
  displaySuffix?: string;
  disabled?: boolean;
  fsx?: SxProps;
  gbkFieldSx?: SxProps;
  multi?: number;
}
interface SelectFieldProps extends BaseFieldProps {
  type: GbkFieldType.SELECT | GbkFieldType.RADIO,
  selectOptions: SelectOption[],
  isOptionEqualToValue?: (a: any, b: any) => boolean,
  valueProcessor?: (a: any) => any
  disableClearable?: boolean
}
interface UploadFieldProps extends BaseFieldProps {
  type: GbkFieldType.UPLOAD
  smallUpload?: boolean;
  allowCamera?: boolean;
  tag?: string;
}
interface CheckboxFieldProps extends BaseFieldProps {
  type: GbkFieldType.CHECKBOX;
  checkboxSx?: SxProps;
}
interface DateTimeFieldProps extends BaseFieldProps {
  type: GbkFieldType.DATE_TIME;
  minDateTime?: DateTime;
}
interface SelectionTabFieldProps extends BaseFieldProps {
  type: GbkFieldType.SELECTION_TABS;
  tabOptions: { display: string, state: boolean }[];
  tabColorRed?: boolean;
  updateFn?: (v: { value: string, index: number }, error?: string) => void;

}
interface GenericFieldProps extends BaseFieldProps {
  type: GbkFieldType.STRING | GbkFieldType.PASSWORD | GbkFieldType.NUMBER | GbkFieldType.DATE | GbkFieldType.SWITCH
}
export type GobikesFieldProps = GenericFieldProps | SelectFieldProps | UploadFieldProps | CheckboxFieldProps | DateTimeFieldProps | SelectionTabFieldProps;

// Field component
export function GobikesField(props: GobikesFieldProps) {

  // Local state
  const [error, setError] = useState('');

  // Parse props and state
  const selectOptions = props.type === GbkFieldType.SELECT || props.type === GbkFieldType.RADIO ? props.selectOptions : [];
  const selectMap = selectOptions.reduce((a, v) => ({ ...a, [v.value]: v }), {} as { [k: string | number]: SelectOption })

  // Update parent state on value change
  function valueChanged(v: any) {
    let validationError;
    if (props.validateFn) {
      validationError = props.validateFn(v);
      setError(validationError);
    }
    if (props.updateFn) props.updateFn(v, validationError);
  }

  // Label content
  let labelContent: ReactNode;
  if (props.useInternalLabel) {
    labelContent = '';
  } else {
    if (props.label) labelContent = props.label;
    switch (props.type) {
      case GbkFieldType.CHECKBOX: {
        labelContent = '';
        break;
      }
      default: {
        if (props.label) labelContent = <Fragment><label className="label">{props.label}</label> <br /></Fragment>;
      }
    }
  }

  // Value content
  let valueContent: ReactNode;
  if (props.editMode) {
    switch (props.type) {
      case GbkFieldType.NUMBER: {
        valueContent = (
          <FormControl sx={props.fsx}>
            <TextField
              InputProps={props.inputProps}
              type='number'
              value={props.value || props.value === 0 ? props.value.toString() : ''}
              size={props.size || 'small'}
              label={props.useInternalLabel ? props.label : undefined}
              onChange={e => {
                return valueChanged(Number(e.target.value));
              }}
              error={!!error}
              helperText={error || ' '}
              disabled={props.disabled}
            />
          </FormControl>
        )
        break;
      }
      case GbkFieldType.STRING: {
        valueContent = (
          <FormControl sx={props.fsx}>
            <TextField
              InputProps={props.inputProps}
              value={props.value}
              size={props.size || 'small'}
              label={props.useInternalLabel ? props.label : undefined}
              onChange={e => valueChanged(e.target.value)}
              error={!!error}
              helperText={error || ' '}
              disabled={props.disabled}
              multiline={Boolean(props.multi)}
              rows={props.multi}
            />
          </FormControl>
        )
        break;
      }
      case GbkFieldType.PASSWORD: {
        valueContent = (
          <FormControl sx={props.fsx}>
            <TextField
              InputProps={props.inputProps}
              value={props.value}
              type="password"
              size={props.size || 'small'}
              label={props.useInternalLabel ? props.label : undefined}
              onChange={e => valueChanged(e.target.value)}
              error={!!error}
              helperText={error || ' '}
              disabled={props.disabled}
              multiline={Boolean(props.multi)}
              rows={props.multi}
            />
          </FormControl>
        )
        break;
      }
      case GbkFieldType.SELECT: {
        valueContent = (
          <FormControl sx={props.fsx}>
            <Autocomplete
              onChange={(e, v) => { if (props.valueProcessor) valueChanged(props.valueProcessor(v)); else valueChanged(v?.id); }}
              options={selectOptions.filter(a => a.value === props.value || !a.invisible).map(a => ({ label: a.display, id: a.value }))}
              isOptionEqualToValue={props.isOptionEqualToValue ? props.isOptionEqualToValue : (o, v) => o.id === v.id}
              disableClearable={props.disableClearable}
              renderInput={(params) => <TextField
                {...params}
                size={props.size || 'small'}
                label={props.useInternalLabel ? props.label : undefined}
              />}
              value={props.value !== undefined && props.value !== null && props.value !== '' ? { label: selectMap[props.value]?.display, id: props.value } : null}
            />
          </FormControl>
        )
        break;
      }
      case GbkFieldType.DATE: {
        valueContent = (
          <FormControl sx={props.fsx}>
            <LocalizationProvider dateAdapter={AdapterLuxon} >
              <DatePicker
                InputProps={props.inputProps}
                renderInput={(props) => <TextField {...props} error={!!error} size={props.size || 'small'} helperText={error || ' '} />}
                onChange={e => valueChanged(e ? e.toMillis() : null)}
                value={DateTime.fromMillis(Number(props.value) || new Date().getTime())}
                disabled={props.disabled}
                inputFormat="dd/MM/yyyy"
              />
            </LocalizationProvider>
          </FormControl>
        )
        break;
      }
      case GbkFieldType.DATE_TIME: {
        valueContent = (
          <FormControl sx={props.fsx}>
            <LocalizationProvider dateAdapter={AdapterLuxon} >
              <DateTimePicker
                inputFormat="dd/MM/yyyy hh:mm a"
                InputProps={props.inputProps}
                renderInput={(props) => <TextField {...props} error={!!error} size={props.size || 'small'} helperText={error || ' '} />}
                onChange={(e: any, a: any) => { const d = e as DateTime; valueChanged(e ? d.set({ second: 0, millisecond: 0 }).toMillis() : null) }}
                value={props.value}
                minDateTime={props.minDateTime}
                disabled={props.disabled}
                minutesStep={30}
                shouldDisableTime={(timeValue, clockType) => {
                  return clockType === "minutes" && timeValue % 30 !== 0;
                }}
                views={['year', 'month', 'day', 'hours', 'minutes']}
              />
            </LocalizationProvider>
          </FormControl>
        )
        break;
      }
      case GbkFieldType.SWITCH: {
        valueContent = (
          <FormControl sx={props.fsx}>
            <Switch
              checked={props.value}
              disabled={props.disabled}
              onChange={(_: any, newState: boolean) => { valueChanged(newState) }}
            />
          </FormControl>
        )
        break;
      }
      case GbkFieldType.UPLOAD: {
        if (!props.value) {
          valueContent = (
            <FormControl sx={props.fsx}>
              <div className={`gbk-upload ${props.smallUpload ? 'small' : ''}`}>
                <label htmlFor={`${props.type}-${props.tag}`}>
                  <span className='gbk-icon-span large'>
                    <UploadOutlined color='primary' /> Upload &nbsp;&nbsp;
                  </span>
                </label>
                {props.allowCamera && (
                  <CameraCaptureButton onCapture={async (file) => {
                    const ans = await resizeImageFn(file);
                    const compressedBase64 = await convertBase64(ans);
                    valueChanged(compressedBase64 || undefined);
                  }} />
                )}
                <input
                  accept='image/jpg,image/png,image/webp'
                  style={{ display: 'none' }}
                  id={`${props.type}-${props.tag}`}
                  type='file'
                  onChange={async (e: any) => {
                    const file = e?.nativeEvent?.target?.files[0];
                    const ans = await resizeImageFn(file);
                    const compressedBase64 = await convertBase64(ans);
                    valueChanged(compressedBase64 || undefined);
                  }}
                />
              </div>
            </FormControl>
          );
        } else {
          valueContent = (
            <div className={`gbk-upload done ${props.smallUpload ? 'small' : ''}`}>
              <div className='gbk-uploaded-img'>
                {
                  props.smallUpload ? (
                    <label className='gbk-icon-span large'>
                      <a className="gbk-icon-span" href={props.value} download={props.label}> <Download /></a> &nbsp;
                      File selected
                    </label>
                  ) : (
                    <img src={props.value} alt={props.label?.toString()} />
                  )
                }
                <button type='button' onClick={() => valueChanged(undefined)}>
                  {props.smallUpload ? <Clear htmlColor={Colors.primary} /> : <Cancel htmlColor={Colors.greyDark1} />}
                </button>
              </div>
            </div>
          )
        }
        break;
      }
      case GbkFieldType.CHECKBOX: {
        valueContent = (
          <FormControl sx={props.fsx}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(props.value)}
                  disabled={props.disabled}
                  size={props.size}
                  sx={props.checkboxSx}
                />}
              label={props.label || ''}
              onChange={(e: any) => valueChanged(e.target.checked)}
            />
          </FormControl>
        )
        break;
      }
      case GbkFieldType.RADIO: {
        valueContent = (
          <FormControl sx={props.fsx}>
            <RadioGroup
              row
              onChange={(e: any) => valueChanged(e.target.value)}
              // defaultChecked={props.value || 0}
              value={props.value}
            >
              {selectOptions.map(o => (
                <FormControlLabel key={o.value} checked={o.value === props.value} value={o.value} control={<Radio />} label={o.display} />
              ))}
            </RadioGroup>
          </FormControl>
        )
        break;
      }
      case GbkFieldType.SELECTION_TABS: {
        valueContent = (
          <div className="gbk-sel-tab">
            {props.tabOptions.map((opt, i) => (
              <div
                className={`gbk-sel-tab-option ${opt.state ? 'selected' : ''} ${opt.state && props.tabColorRed ? 'red-tab' : ''}`}
                onClick={() => {
                  valueChanged({ value: opt.display, index: i });
                }}
              >
                {opt.display}
              </div>
            ))}
          </div>
        )
        break;
      }
    }
  }
  // Edit mode off
  else {
    switch (props.type) {
      case GbkFieldType.DATE: {
        valueContent = props.value ? DateTime.fromMillis(Number(props.value)).toLocaleString({ ...DateTime.DATETIME_MED, hourCycle: "h12" }) : '-'
        break;
      }
      case GbkFieldType.DATE_TIME: {
        valueContent = props.value ? DateTime.fromMillis(Number(props.value)).toLocaleString({ ...DateTime.DATETIME_MED, hourCycle: "h12" }) : '-'
        break;
      }
      case GbkFieldType.SELECT: {
        valueContent = props.value !== undefined && props.value !== null && props.value !== '' ? selectMap[props.value]?.display : '-';
        break;
      }
      case GbkFieldType.CHECKBOX: {
        valueContent = (
          <FormControl sx={props.fsx}>
            <FormControlLabel
              control={<Checkbox checked={Boolean(props.value)} disabled={props.disabled} />}
              label={props.label || ''}
            />
          </FormControl>
        )
        break;
      }
      case GbkFieldType.RADIO: {
        valueContent = (
          <FormControl sx={props.fsx} key={props.editMode.toString()} >
            <RadioGroup
              row
              key={props.editMode.toString()}
            >
              {selectOptions.map(o => (
                <FormControlLabel disabled key={o.value} checked={o.value === props.value} value={o.value} control={<Radio />} label={o.display} />
              ))}
            </RadioGroup>
          </FormControl>
        )
        break;
      }
      case GbkFieldType.SWITCH: {
        valueContent = (
          <FormControl sx={props.fsx}>
            <Switch
              checked={props.value}
              disabled={true}
            />
          </FormControl>
        )
        break;
      }
      case GbkFieldType.UPLOAD: {
        if (!props.value) {
          valueContent = (
            <div className={`gbk-upload disabled ${props.smallUpload ? 'small' : ''}`}>
              <label htmlFor={`${props.type}-${props.tag}`}>
                <span className='gbk-icon-span large'>
                  No file uploaded
                </span>
              </label>
            </div>
          )
        }
        else if (typeof props.value === 'string') {
          valueContent = <div className={`gbk-upload done ${props.smallUpload ? 'small' : ''}`}>
            <div className='gbk-uploaded-img'>
              {
                props.smallUpload ? (
                  <label className='gbk-icon-span large'>
                    <a className="gbk-icon-span" href={props.value} download={props.label}> <Download /></a> &nbsp;
                    File selected
                  </label>
                ) : (
                  <img src={props.value} alt={props.label?.toString()} />
                )
              }
            </div>
          </div>
        } else if (Array.isArray(props.value)) {
          valueContent = props.value.map(v =>
            <img className="small-image" src={(v as UploadModel).publicUrl} alt={v.label} />
          )
        } else {
          valueContent = <img className="small-image" src={(props.value as UploadModel).publicUrl} alt={props.value.label} />;
        }
        break;
      }
      case GbkFieldType.NUMBER: {
        valueContent = `${props.displayPrefix && props.value ? props.displayPrefix : ''} ${props.value || '-'} ${props.displaySuffix && props.value ? props.displaySuffix : ''}`;
        break;
      }
      case GbkFieldType.SELECTION_TABS: {
        valueContent = (
          <div className="gbk-sel-tab disabled">
            {props.tabOptions.map((opt, i) => (
              <div
                className={`gbk-sel-tab-option ${opt.state ? 'selected' : ''} ${opt.state && props.tabColorRed ? 'red-tab' : ''}`}
              >
                {opt.display}
              </div>
            ))}
          </div>
        )
        break;
      }
      default: {
        valueContent = `${props.displayPrefix && props.value ? props.displayPrefix : ''} ${props.value === 0 ? props.value : props.value || '-'} ${props.displaySuffix && props.value ? props.displaySuffix : ''}`;
        break;
      }
    }
  }

  // Make value DOM
  let valueDOM;
  switch (props.type) {
    case GbkFieldType.CHECKBOX: {
      valueDOM = <div className={`value`}>{valueContent}</div>;
      break;
    }
    case GbkFieldType.SWITCH: {
      valueDOM = <div className={`value}`} style={{ paddingTop: 3 }}>{valueContent}</div>;
      break;
    }
    case GbkFieldType.SELECTION_TABS: {
      valueDOM = <div className={`value`} style={{ width: 'auto' }}>{valueContent}</div>;
      break;
    }
    default: {
      valueDOM = <div className={`value ${props.editMode ? '' : 'extra-padding'}`}>{valueContent}</div>;
    }
  }

  // Return final DOM
  return (
    <Box className="gbk-field" sx={props.gbkFieldSx}>
      {labelContent}
      {valueDOM}
    </Box>
  )

}
